import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  forwardRef,
  useId,
} from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaChevronDown, FaPlus } from "react-icons/fa";

export interface AutocompleteOption {
  label: string;
  value: string;
  [key: string]: any;
}

export interface AutocompleteProps<T = AutocompleteOption> {
  /** Options to display in the dropdown */
  options: T[];
  /** Currently selected value(s) */
  value?: T | T[] | null;
  /** Callback when value changes */
  onChange?: (value: T | T[] | null) => void;
  /** Callback when input text changes */
  onInputChange?: (value: string) => void;
  /** Allow multiple selections */
  multiple?: boolean;
  /** Allow free text input (create new options) */
  freeSolo?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Label for the input */
  label?: string;
  /** Error message */
  error?: string;
  /** Helper text */
  helperText?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Full width */
  fullWidth?: boolean;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Get option label for display */
  getOptionLabel?: (option: T) => string;
  /** Get option value for comparison */
  getOptionValue?: (option: T) => string;
  /** Check if option is equal to value */
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  /** Render custom option */
  renderOption?: (option: T, state: { selected: boolean }) => React.ReactNode;
  /** Render custom tags for multiple selection */
  renderTags?: (
    value: T[],
    getTagProps: (index: number) => {
      key: string;
      onDelete: () => void;
    }
  ) => React.ReactNode;
  /** Filter options based on input */
  filterOptions?: (options: T[], inputValue: string) => T[];
  /** Custom class name */
  className?: string;
  /** No options text */
  noOptionsText?: string;
  /** Loading text */
  loadingText?: string;
  /** Create option text (for freeSolo) */
  createOptionText?: string;
  /** Open on focus */
  openOnFocus?: boolean;
  /** Clear on escape */
  clearOnEscape?: boolean;
  /** Auto highlight first option */
  autoHighlight?: boolean;
  /** Disable portal (render inline) */
  disablePortal?: boolean;
  /** Input props */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

/**
 * Arc-style Autocomplete component
 *
 * Features:
 * - Single and multiple selection
 * - Free text input (freeSolo)
 * - Custom option rendering
 * - Keyboard navigation
 * - Portal-based dropdown
 * - Smooth animations
 */
function AutocompleteInner<T = AutocompleteOption>(
  {
    options,
    value,
    onChange,
    onInputChange,
    multiple = false,
    freeSolo = false,
    placeholder = "Search...",
    label,
    error,
    helperText,
    disabled = false,
    loading = false,
    fullWidth = true,
    size = "md",
    getOptionLabel = (option: T) =>
      typeof option === "string"
        ? option
        : (option as any)?.label || String(option),
    getOptionValue = (option: T) =>
      typeof option === "string"
        ? option
        : (option as any)?.value || (option as any)?.label || String(option),
    isOptionEqualToValue = (option: T, val: T) =>
      getOptionValue(option) === getOptionValue(val),
    renderOption,
    renderTags,
    filterOptions,
    className = "",
    noOptionsText = "No options",
    loadingText = "Loading...",
    createOptionText = "Create",
    openOnFocus = true,
    clearOnEscape = true,
    autoHighlight = true,
    disablePortal = false,
    inputProps,
  }: AutocompleteProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const generatedId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Merge refs
  const mergedRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref]
  );

  // Get selected values as array
  const selectedValues = useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Filter options based on input
  const filteredOptions = useMemo(() => {
    if (filterOptions) {
      return filterOptions(options, inputValue);
    }

    if (!inputValue) return options;

    const lowerInput = inputValue.toLowerCase();
    return options.filter((option) =>
      getOptionLabel(option).toLowerCase().includes(lowerInput)
    );
  }, [options, inputValue, filterOptions, getOptionLabel]);

  // Check if option is selected
  const isSelected = useCallback(
    (option: T) => {
      return selectedValues.some((val) => isOptionEqualToValue(option, val));
    },
    [selectedValues, isOptionEqualToValue]
  );

  // Check if we should show "create" option
  const showCreateOption = useMemo(() => {
    if (!freeSolo || !inputValue.trim()) return false;

    const exists = options.some(
      (opt) => getOptionLabel(opt).toLowerCase() === inputValue.toLowerCase()
    );

    return !exists;
  }, [freeSolo, inputValue, options, getOptionLabel]);

  // Update dropdown position
  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 300; // Max height

    // Check if dropdown should open upward
    const spaceBelow = viewportHeight - rect.bottom;
    const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow;

    setDropdownPosition({
      top: openUpward ? rect.top - dropdownHeight : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  // Handle opening dropdown
  const openDropdown = useCallback(() => {
    if (disabled) return;
    updatePosition();
    setIsOpen(true);
    if (autoHighlight && filteredOptions.length > 0) {
      setHighlightedIndex(0);
    }
  }, [disabled, updatePosition, autoHighlight, filteredOptions.length]);

  // Handle closing dropdown
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  // Handle option selection
  const selectOption = useCallback(
    (option: T) => {
      if (multiple) {
        const newValue = isSelected(option)
          ? selectedValues.filter((val) => !isOptionEqualToValue(option, val))
          : [...selectedValues, option];
        onChange?.(newValue as T[]);
      } else {
        onChange?.(option);
        setInputValue(getOptionLabel(option));
        closeDropdown();
      }
    },
    [
      multiple,
      isSelected,
      selectedValues,
      isOptionEqualToValue,
      onChange,
      getOptionLabel,
      closeDropdown,
    ]
  );

  // Handle creating new option (freeSolo)
  const createOption = useCallback(() => {
    if (!inputValue.trim()) return;

    const newOption = { label: inputValue, value: inputValue } as T;

    if (multiple) {
      onChange?.([...selectedValues, newOption] as T[]);
    } else {
      onChange?.(newOption);
      closeDropdown();
    }

    setInputValue("");
  }, [inputValue, multiple, selectedValues, onChange, closeDropdown]);

  // Handle removing a tag (multiple mode)
  const removeTag = useCallback(
    (index: number) => {
      const newValue = selectedValues.filter((_, i) => i !== index);
      onChange?.(multiple ? (newValue as T[]) : null);
    },
    [selectedValues, onChange, multiple]
  );

  // Handle input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      onInputChange?.(newValue);

      if (!isOpen) {
        openDropdown();
      }
    },
    [isOpen, openDropdown, onInputChange]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (!isOpen) {
            openDropdown();
          } else {
            const maxIndex = showCreateOption
              ? filteredOptions.length
              : filteredOptions.length - 1;
            setHighlightedIndex((prev) => Math.min(prev + 1, maxIndex));
          }
          break;

        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          break;

        case "Enter":
          e.preventDefault();
          if (isOpen && highlightedIndex >= 0) {
            if (
              showCreateOption &&
              highlightedIndex === filteredOptions.length
            ) {
              createOption();
            } else if (filteredOptions[highlightedIndex]) {
              selectOption(filteredOptions[highlightedIndex]);
            }
          } else if (freeSolo && inputValue.trim()) {
            createOption();
          }
          break;

        case "Escape":
          if (clearOnEscape) {
            closeDropdown();
            if (!multiple) {
              setInputValue("");
            }
          }
          break;

        case "Backspace":
          if (multiple && !inputValue && selectedValues.length > 0) {
            removeTag(selectedValues.length - 1);
          }
          break;
      }
    },
    [
      isOpen,
      openDropdown,
      highlightedIndex,
      filteredOptions,
      showCreateOption,
      selectOption,
      createOption,
      freeSolo,
      inputValue,
      clearOnEscape,
      closeDropdown,
      multiple,
      selectedValues,
      removeTag,
    ]
  );

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, closeDropdown]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    const handleUpdate = () => updatePosition();
    window.addEventListener("scroll", handleUpdate, true);
    window.addEventListener("resize", handleUpdate);

    return () => {
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
    };
  }, [isOpen, updatePosition]);

  // Size styles
  const sizeStyles = {
    sm: {
      input: "py-1.5 px-3 text-sm",
      tag: "text-xs px-1.5 py-0.5",
      option: "py-1.5 px-3 text-sm",
    },
    md: {
      input: "py-2 px-3 text-sm",
      tag: "text-xs px-2 py-1",
      option: "py-2 px-3 text-sm",
    },
    lg: {
      input: "py-2.5 px-4 text-base",
      tag: "text-sm px-2.5 py-1",
      option: "py-2.5 px-4 text-base",
    },
  };

  const currentSize = sizeStyles[size];

  // Render tags for multiple selection
  const renderSelectedTags = () => {
    if (!multiple || selectedValues.length === 0) return null;

    if (renderTags) {
      return renderTags(selectedValues, (index) => ({
        key: `tag-${index}`,
        onDelete: () => removeTag(index),
      }));
    }

    return selectedValues.map((val, index) => (
      <span
        key={`tag-${index}`}
        className={`
          inline-flex items-center gap-1
          bg-accent-muted text-accent-dark
          rounded-arc-full
          ${currentSize.tag}
        `}
      >
        <span className="truncate max-w-[150px]">{getOptionLabel(val)}</span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            removeTag(index);
          }}
          className="p-0.5 hover:bg-accent/20 rounded-full transition-colors"
        >
          <FaTimes className="w-2.5 h-2.5" />
        </button>
      </span>
    ));
  };

  // Render dropdown content
  const renderDropdown = () => {
    const content = (
      <div
        ref={dropdownRef}
        className={`
          bg-surface-elevated
          border border-arc-border
          rounded-arc-md
          shadow-arc-lg
          overflow-hidden
          animate-arc-fade-in
        `}
        style={
          disablePortal
            ? {}
            : {
                position: "fixed",
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                zIndex: 9999,
              }
        }
      >
        <div className="max-h-[300px] overflow-y-auto">
          {loading ? (
            <div className="py-4 px-3 text-center text-content-secondary">
              <span className="inline-block animate-spin mr-2">⏳</span>
              {loadingText}
            </div>
          ) : filteredOptions.length === 0 && !showCreateOption ? (
            <div className="py-4 px-3 text-center text-content-tertiary">
              {noOptionsText}
            </div>
          ) : (
            <>
              {filteredOptions.map((option, index) => {
                const selected = isSelected(option);
                const highlighted = index === highlightedIndex;

                return (
                  <div
                    key={getOptionValue(option)}
                    onClick={() => selectOption(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`
                      cursor-pointer
                      transition-colors duration-arc-fast
                      ${currentSize.option}
                      ${
                        selected
                          ? highlighted
                            ? "bg-accent-pressed"
                            : "bg-accent-pressed"
                          : highlighted
                            ? "bg-accent-hover"
                            : "hover:bg-accent-hover"
                      }
                      ${selected ? "text-accent font-medium" : "text-content-primary"}
                    `}
                  >
                    {renderOption ? (
                      renderOption(option, { selected })
                    ) : (
                      <div className="flex items-center justify-between">
                        <span>{getOptionLabel(option)}</span>
                        {selected && (
                          <svg
                            className="w-4 h-4 text-accent"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {showCreateOption && (
                <div
                  onClick={createOption}
                  onMouseEnter={() =>
                    setHighlightedIndex(filteredOptions.length)
                  }
                  className={`
                    cursor-pointer
                    transition-colors duration-arc-fast
                    ${currentSize.option}
                    ${
                      highlightedIndex === filteredOptions.length
                        ? "bg-accent-hover"
                        : "hover:bg-accent-hover"
                    }
                    text-accent font-medium
                    border-t border-arc-border
                  `}
                >
                  <div className="flex items-center gap-2">
                    <FaPlus className="w-3 h-3" />
                    <span>
                      {createOptionText} "{inputValue}"
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );

    if (disablePortal) {
      return content;
    }

    return typeof window !== "undefined"
      ? createPortal(content, document.body)
      : null;
  };

  return (
    <div className={`${fullWidth ? "w-full" : ""} ${className}`}>
      {label && (
        <label
          htmlFor={generatedId}
          className="block mb-1.5 text-sm font-semibold text-content-primary"
        >
          {label}
        </label>
      )}

      <div
        ref={containerRef}
        className={`
          relative
          flex flex-wrap items-center gap-1
          bg-surface-elevated
          border rounded-arc-md
          transition-all duration-arc-fast
          ${currentSize.input}
          ${
            error
              ? "border-error focus-within:border-error focus-within:ring-2 focus-within:ring-error-main/20"
              : "border-arc-border hover:border-arc-border-strong focus-within:border-transparent focus-within:ring-2 focus-within:ring-accent"
          }
          ${disabled ? "opacity-50 cursor-not-allowed bg-surface-secondary" : ""}
        `}
        onClick={() => {
          if (!disabled) {
            inputRef.current?.focus();
            if (openOnFocus) openDropdown();
          }
        }}
      >
        {renderSelectedTags()}

        <input
          ref={mergedRef}
          id={generatedId}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => openOnFocus && openDropdown()}
          onKeyDown={handleKeyDown}
          placeholder={selectedValues.length === 0 ? placeholder : ""}
          disabled={disabled}
          className={`
            flex-1 min-w-[60px]
            bg-transparent
            outline-none
            text-content-primary
            placeholder:text-content-tertiary
            ${disabled ? "cursor-not-allowed" : ""}
          `}
          {...inputProps}
        />

        <div className="flex items-center gap-1 ml-auto">
          {selectedValues.length > 0 && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(multiple ? ([] as T[]) : null);
                setInputValue("");
              }}
              className="p-1 text-content-tertiary hover:text-content-secondary transition-colors"
            >
              <FaTimes className="w-3 h-3" />
            </button>
          )}

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              isOpen ? closeDropdown() : openDropdown();
            }}
            disabled={disabled}
            className={`
              p-1 text-content-tertiary hover:text-content-secondary
              transition-transform duration-arc-fast
              ${isOpen ? "rotate-180" : ""}
              ${disabled ? "cursor-not-allowed" : ""}
            `}
          >
            <FaChevronDown className="w-3 h-3" />
          </button>
        </div>
      </div>

      {isOpen && renderDropdown()}

      {(error || helperText) && (
        <p
          className={`mt-1.5 text-xs ${error ? "text-error" : "text-content-tertiary"}`}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
}

// ForwardRef with generics
const Autocomplete = forwardRef(AutocompleteInner) as <
  T = AutocompleteOption,
>(
  props: AutocompleteProps<T> & { ref?: React.ForwardedRef<HTMLInputElement> }
) => React.ReactElement;

export { Autocomplete };
export default Autocomplete;









